import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Linkedin, Twitter, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_green-consulting/artifacts/7uexgx6g_png-01.png";

export const Footer = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success('Successfully subscribed to newsletter!');
      setEmail('');
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Email already subscribed');
      } else {
        toast.error('Failed to subscribe. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer data-testid="footer" className="footer-gradient text-white">
      {/* Newsletter Section */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="font-serif text-3xl md:text-4xl mb-4">
                Stay Updated on Sustainability
              </h3>
              <p className="font-sans text-white/70 text-base">
                Get the latest insights on ESG, carbon markets, and climate finance delivered to your inbox.
              </p>
            </div>
            <form onSubmit={handleNewsletterSubmit} className="flex gap-3" data-testid="newsletter-form">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="newsletter-email-input"
                className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-white/50 font-sans h-12"
              />
              <Button 
                type="submit" 
                disabled={isSubmitting}
                data-testid="newsletter-submit-button"
                className="btn-gradient text-white font-sans px-6 h-12"
              >
                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6">
              <img 
                src={LOGO_URL} 
                alt="Maple Integrated Solutions" 
                className="h-14 w-auto bg-white rounded-lg p-2"
              />
            </Link>
            <p className="font-sans text-white/70 text-sm leading-relaxed mb-6">
              Helping businesses get climate-ready, today and tomorrow. Your trusted partner for sustainable transformation.
            </p>
            <div className="flex gap-4">
              <a 
                href="https://linkedin.com" 
                target="_blank" 
                rel="noopener noreferrer"
                data-testid="social-linkedin"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-brand-green transition-colors"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a 
                href="https://twitter.com" 
                target="_blank" 
                rel="noopener noreferrer"
                data-testid="social-twitter"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-brand-green transition-colors"
              >
                <Twitter className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Services Column */}
          <div>
            <h4 className="font-serif text-lg mb-6">Services</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/services#esg" className="font-sans text-sm text-white/70 hover:text-white transition-colors">
                  ESG Advisory
                </Link>
              </li>
              <li>
                <Link to="/services#carbon" className="font-sans text-sm text-white/70 hover:text-white transition-colors">
                  Carbon Market Advisory
                </Link>
              </li>
              <li>
                <Link to="/services#climate" className="font-sans text-sm text-white/70 hover:text-white transition-colors">
                  Climate Finance
                </Link>
              </li>
              <li>
                <Link to="/services#supply-chain" className="font-sans text-sm text-white/70 hover:text-white transition-colors">
                  Sustainable Supply Chain
                </Link>
              </li>
              <li>
                <Link to="/services#digital" className="font-sans text-sm text-white/70 hover:text-white transition-colors">
                  Digital Solutions
                </Link>
              </li>
            </ul>
          </div>

          {/* Company Column */}
          <div>
            <h4 className="font-serif text-lg mb-6">Company</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/about" className="font-sans text-sm text-white/70 hover:text-white transition-colors">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/projects" className="font-sans text-sm text-white/70 hover:text-white transition-colors">
                  Projects
                </Link>
              </li>
              <li>
                <Link to="/blog" className="font-sans text-sm text-white/70 hover:text-white transition-colors">
                  Blog
                </Link>
              </li>
              <li>
                <Link to="/contact" className="font-sans text-sm text-white/70 hover:text-white transition-colors">
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Column */}
          <div>
            <h4 className="font-serif text-lg mb-6">Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-brand-green flex-shrink-0 mt-0.5" />
                <span className="font-sans text-sm text-white/70">
                  Mumbai, India
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-brand-green flex-shrink-0" />
                <a 
                  href="mailto:info@mapleintegratedsolutions.com"
                  className="font-sans text-sm text-white/70 hover:text-white transition-colors"
                >
                  info@mapleintegratedsolutions.com
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-brand-green flex-shrink-0" />
                <a 
                  href="tel:+917738481009"
                  className="font-sans text-sm text-white/70 hover:text-white transition-colors"
                >
                  +91 77384 81009
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="font-sans text-sm text-white/50">
              © {new Date().getFullYear()} Maple Integrated Solutions. All rights reserved.
            </p>
            <p className="font-sans text-sm text-white/50">
              Registered under MSME - Ministry of MSME, Govt. of India
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};
